import React from 'react';
import Grid from '@mui/material/Grid';
import clsx from 'clsx';
import makeStyles from '@mui/styles/makeStyles';
import { useRouter } from 'next/router';
import ProductId from '../../common/ProductId';
import MenuTile from '../MenuTile/MenuTile';
import { formatTestId } from '@/common/string-formatter';
import { MenuRecipe } from '../pizza/pizzaMenuTypes';
import useOneClickAnalytics from '@/homepage/HutFavoritesSection/hooks/useOneClickAnalytics';
import Routes from '@/router/routes';

const styles = makeStyles(() => ({
  root: {
    paddingBottom: 30
  },
  hiddenTile: {
    display: 'none'
  }
}));

interface MenuTileListProps {
  currentRoute?: string;
  tileList: MenuRecipe[];
  handleClick: (tile: MenuRecipe, index?: number) => void;
  isLocalized?: boolean;
  hasLinkEnabled: boolean;
  tileRef?: React.MutableRefObject<HTMLElement[]>;
  testId?: string;
  hideDescription?: boolean;
  showPricePrefix?: boolean;
  expandedList?: boolean;
  minimumDisplayedItemsQuantity?: number;
  handleInfoClick?: (tile: MenuRecipe) => void;
  handleInfoDrawerLoaded?: (tile: MenuRecipe, position: number) => void;
  onGetStartedLinkClick?: () => void;
}

const MenuTileList = (props: MenuTileListProps): JSX.Element => {
  const {
    tileList,
    hasLinkEnabled,
    isLocalized,
    hideDescription,
    handleClick,
    tileRef,
    testId,
    showPricePrefix = true,
    expandedList = true,
    minimumDisplayedItemsQuantity = 0,
    handleInfoClick,
    handleInfoDrawerLoaded,
    currentRoute
  } = props;
  const classes = styles();

  const { onGetStartedLinkClick } = useOneClickAnalytics();
  const router = useRouter();
  const isDealsFlow = router.asPath?.includes(Routes.DEALS);


  const testIdPrefix = isLocalized ? 'localized' : 'national';

  const TileList = (
    <div
      data-testid={testId}
      className={classes.root}
    >
      {tileList.map((tile, index) => {
        const {
          id, name, priority, outOfStock, isNew
        } = tile;
        const { globalId } = new ProductId(id);
        const tileName = formatTestId(name);
        const handleTileClick = () => {
          handleClick(tile, index);
        };

        const onInfoClick = () => {
          if (handleInfoClick) handleInfoClick(tile);
        };
        const onInfoDrawerLoaded = () => {
          if (handleInfoDrawerLoaded) handleInfoDrawerLoaded(tile, index);
        };

        const isHidden = !expandedList && index >= minimumDisplayedItemsQuantity;

        // show popular badge if it's a nationalized deals flow and it's the first item when cheesy bites is not enabled
        // or if it's a nationalized pizza menu page and it's the first item
        const showPopularBadge = isDealsFlow ? false : (!isLocalized && priority === 0);

        // show new badge if it's a nationalized deals flow and it's the first item
        const showNewBadge = isDealsFlow && !isLocalized && isNew;

        return (
          <Grid
            container
            data-testid={`menu-tile-container-${tileName}`}
            key={globalId}
            // eslint-disable-next-line no-param-reassign
            ref={(element) => {
              if (element && tileRef) tileRef.current[index] = element;
            }}
            className={clsx(isHidden && classes.hiddenTile)}
          >
            <MenuTile
              {...tile}
              currentRoute={currentRoute}
              name={tile.name}
              imageURL={tile.imageURL || ''}
              hideDescription={hideDescription}
              testId={`${testIdPrefix}-menu-tile-${tileName}`}
              handleClick={handleTileClick}
              hasLinkEnabled={outOfStock ? false : hasLinkEnabled}
              showPricePrefix={showPricePrefix}
              handleInfoClick={onInfoClick}
              handleInfoDrawerLoaded={onInfoDrawerLoaded}
              productId={tile.id}
              onGetStartedLinkClick={() => onGetStartedLinkClick(tile, index)}
              badges={{
                soldOut: outOfStock,
                vegetarian: tile?.vegetarian,
                popular: showPopularBadge,
                newBadge: showNewBadge
              }}
            />
          </Grid>
        );
      })}
    </div>
  );

  return TileList;
};
export default MenuTileList;
